import { useEffect, useRef, useState } from "react";
import {
  downloadSyncDocument,
  importSyncDocument,
  syncWithGoogleDrive,
  syncWithRemoteObjectStore,
} from "../../state/sync-actions.js";
import {
  DropboxObjectStore,
  GitHubObjectStore,
  OneDriveObjectStore,
  PCloudObjectStore,
  S3CompatibleObjectStore,
  UnsupportedDirectObjectStore,
  WebDavObjectStore,
} from "../../platform/sync/cloud-object-stores.js";
import { triggerAutoSyncNow } from "../../state/auto-sync.js";
import { storage } from "../../state/storage.js";
import { identity } from "../../state/identity.js";
import { useT } from "../../i18n/use-t.js";
import { SyncConflictModal } from "./SyncConflictModal.js";
import {
  parseRcloneConfig,
  STORAGE_PROVIDER_CATALOG,
  type ConflictResolution,
  type IdentitySession,
  type RemoteObjectStore,
  type Settings,
  type SyncConflict,
} from "@roqeet/core";

type DirectProviderId =
  | "github"
  | "webdav"
  | "dropbox"
  | "onedrive"
  | "pcloud"
  | "s3-compatible"
  | "mega"
  | "proton-drive";

interface ExternalSyncProfile {
  provider: DirectProviderId;
  rootPath: string;
  accessToken: string;
  username: string;
  password: string;
  bearerToken: string;
  baseUrl: string;
  owner: string;
  repo: string;
  branch: string;
  endpoint: string;
  bucket: string;
  region: string;
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  driveId: string;
  apiBaseUrl: string;
  forcePathStyle: boolean;
}

const EXTERNAL_SYNC_PROFILE_KEY = "roqeet.externalSync.profile.v1";
const DIRECT_PROVIDER_IDS: DirectProviderId[] = [
  "github",
  "webdav",
  "dropbox",
  "onedrive",
  "pcloud",
  "s3-compatible",
  "mega",
  "proton-drive",
];
const syncInputCls = "w-full rounded-lg border border-line bg-bg px-2 py-1.5 text-xs text-ink outline-none focus:border-accent";
const syncLabelCls = "space-y-1 text-xs text-ink-faint";

function defaultExternalProfile(): ExternalSyncProfile {
  return {
    provider: "github",
    rootPath: "RoQeet",
    accessToken: "",
    username: "",
    password: "",
    bearerToken: "",
    baseUrl: "",
    owner: "",
    repo: "",
    branch: "main",
    endpoint: "",
    bucket: "",
    region: "auto",
    accessKeyId: "",
    secretAccessKey: "",
    sessionToken: "",
    driveId: "",
    apiBaseUrl: "",
    forcePathStyle: true,
  };
}

function loadExternalProfile(): ExternalSyncProfile {
  if (typeof localStorage === "undefined") return defaultExternalProfile();
  try {
    const raw = localStorage.getItem(EXTERNAL_SYNC_PROFILE_KEY);
    if (!raw) return defaultExternalProfile();
    return { ...defaultExternalProfile(), ...(JSON.parse(raw) as Partial<ExternalSyncProfile>) };
  } catch {
    return defaultExternalProfile();
  }
}

function saveExternalProfile(profile: ExternalSyncProfile): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(EXTERNAL_SYNC_PROFILE_KEY, JSON.stringify(profile));
}

function createExternalObjectStore(profile: ExternalSyncProfile): RemoteObjectStore {
  switch (profile.provider) {
    case "github":
      return new GitHubObjectStore({
        token: required(profile.accessToken, "GitHub token"),
        owner: required(profile.owner, "GitHub owner"),
        repo: required(profile.repo, "GitHub repo"),
        branch: profile.branch.trim() || "main",
        rootPath: profile.rootPath.trim() || undefined,
      });
    case "webdav":
      return new WebDavObjectStore({
        baseUrl: required(profile.baseUrl, "WebDAV URL"),
        rootPath: profile.rootPath.trim() || undefined,
        bearerToken: profile.bearerToken.trim() || undefined,
        username: profile.username.trim() || undefined,
        password: profile.password || undefined,
      });
    case "dropbox":
      return new DropboxObjectStore({
        accessToken: required(profile.accessToken, "Dropbox access token"),
        rootPath: profile.rootPath.trim() || undefined,
      });
    case "onedrive":
      return new OneDriveObjectStore({
        accessToken: required(profile.accessToken, "OneDrive access token"),
        rootPath: profile.rootPath.trim() || undefined,
        driveId: profile.driveId.trim() || undefined,
      });
    case "pcloud":
      return new PCloudObjectStore({
        accessToken: required(profile.accessToken, "pCloud access token"),
        rootPath: profile.rootPath.trim() || undefined,
        apiBaseUrl: profile.apiBaseUrl.trim() || undefined,
      });
    case "s3-compatible":
      return new S3CompatibleObjectStore({
        endpoint: required(profile.endpoint, "S3 endpoint"),
        bucket: required(profile.bucket, "S3 bucket"),
        region: profile.region.trim() || "auto",
        accessKeyId: required(profile.accessKeyId, "S3 access key"),
        secretAccessKey: required(profile.secretAccessKey, "S3 secret key"),
        sessionToken: profile.sessionToken.trim() || undefined,
        rootPath: profile.rootPath.trim() || undefined,
        forcePathStyle: profile.forcePathStyle,
      });
    case "mega":
      return new UnsupportedDirectObjectStore("mega");
    case "proton-drive":
      return new UnsupportedDirectObjectStore("proton-drive");
  }
}

function required(value: string, label: string): string {
  const trimmed = value.trim();
  if (!trimmed) throw new Error(`${label} is required.`);
  return trimmed;
}

function profileFromRcloneRemote(
  current: ExternalSyncProfile,
  remote: ReturnType<typeof parseRcloneConfig>[number],
): ExternalSyncProfile {
  const provider = remote.providerId;
  if (!provider || !DIRECT_PROVIDER_IDS.includes(provider as DirectProviderId)) return current;
  const next = { ...current, provider: provider as DirectProviderId };
  const values = remote.values;
  next.rootPath = values["root_folder"] || values["root_path"] || values["directory"] || current.rootPath;

  if (provider === "webdav") {
    next.baseUrl = values["url"] || next.baseUrl;
    next.username = values["user"] || values["username"] || next.username;
    next.password = values["pass"] || values["password"] || next.password;
  }
  if (provider === "s3-compatible") {
    next.endpoint = values["endpoint"] || next.endpoint;
    next.bucket = values["bucket"] || next.bucket;
    next.region = values["region"] || next.region;
    next.accessKeyId = values["access_key_id"] || values["access_key"] || next.accessKeyId;
    next.secretAccessKey = values["secret_access_key"] || values["secret_key"] || next.secretAccessKey;
  }
  if (provider === "dropbox" || provider === "onedrive" || provider === "pcloud") {
    next.accessToken = values["token"] || values["access_token"] || next.accessToken;
  }
  return next;
}

/**
 * 동기화 UI(결정 #20/#8). 두 가지 경로를 제공한다:
 *  1) 파일 동기화 — OAuth 없이 동기화 문서를 파일로 주고받아 3-way 병합(덮어쓰기 아님).
 *  2) Google Drive — 본인 Drive appDataFolder 파일에 같은 문서를 싣고 병합. 우리 서버는
 *     데이터를 보지 않는다(결정 #8). Google 클라이언트 ID(VITE_GOOGLE_CLIENT_ID)가 빌드에
 *     주입돼 있어야 활성화된다.
 *
 * 두 경로 모두 동일한 병합 엔진(core runSync) + 문서 포맷을 공유한다 — 전송만 다르다.
 */
export function SyncSection() {
  const { t, locale } = useT();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [session, setSession] = useState<IdentitySession>(identity.current());
  const [settings, setSettings] = useState<Settings | undefined>(undefined);
  const [pendingConflicts, setPendingConflicts] = useState<{
    conflicts: SyncConflict[];
    resolve: (res: Map<string, ConflictResolution>) => void;
  } | null>(null);
  const [externalProfile, setExternalProfile] = useState<ExternalSyncProfile>(() => loadExternalProfile());
  const [rcloneText, setRcloneText] = useState("");
  const [rcloneRemotes, setRcloneRemotes] = useState<ReturnType<typeof parseRcloneConfig>>([]);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => identity.subscribe(setSession), []);
  useEffect(() => saveExternalProfile(externalProfile), [externalProfile]);
  useEffect(() => {
    let mounted = true;
    const fetchSettings = () => {
      void storage.getSettings().then((s) => {
        if (mounted) setSettings(s);
      });
    };
    fetchSettings();
    const interval = setInterval(fetchSettings, 3000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  const googleConfigured = identity.isConfigured();
  const connected = session.tier === "google";
  // 자동 동기화는 설정 두 플래그(enabled && auto)를 함께 켜는 단일 스위치로 노출한다
  // (core 게이트와 정렬, 결정 #20). Google 연결돼 있어야 실제로 동작한다.
  const autoOn = !!settings?.sync.enabled && !!settings?.sync.auto;
  const lastSyncedAt = settings?.sync.lastSyncedAt;

  const onToggleAuto = async (v: boolean) => {
    const current = await storage.getSettings();
    const next: Settings = {
      ...current!,
      sync: { ...current!.sync, auto: v, enabled: v ? true : current!.sync.enabled },
    } as any;
    setSettings(next);
    await storage.putSettings(next);
    // 막 켰다면 즉시 한 번 맞춘다(디바운스 생략). 연결 전이면 게이트가 막아 무해.
    if (v) {
      void triggerAutoSyncNow().catch(() => {
        /* 자동 경로라 조용히 무시 */
      });
    }
  };

  const onChangeInterval = async (ms: number) => {
    const current = await storage.getSettings();
    const next: Settings = {
      ...current!,
      sync: { ...current!.sync, debounceMs: ms },
    } as any;
    setSettings(next);
    await storage.putSettings(next);
  };

  const onExport = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await downloadSyncDocument();
      setMsg(t("sync.exportDone"));
    } catch (e) {
      setMsg(t("sync.exportFail", { error: e instanceof Error ? e.message : String(e) }));
    } finally {
      setBusy(false);
    }
  };

  const resolveConflictsAsync = (conflicts: SyncConflict[]) => {
    return new Promise<Map<string, ConflictResolution>>((resolve) => {
      setPendingConflicts({ conflicts, resolve });
    });
  };

  const handleResolveConflicts = (res: Map<string, ConflictResolution>) => {
    if (pendingConflicts) {
      pendingConflicts.resolve(res);
      setPendingConflicts(null);
    }
  };

  const handleCancelConflicts = () => {
    if (pendingConflicts) {
      const res = new Map(pendingConflicts.conflicts.map(c => [c.id, "keep-local" as ConflictResolution]));
      pendingConflicts.resolve(res);
      setPendingConflicts(null);
      setMsg(t("sync.conflictCancelled") || "Conflict resolution cancelled. Kept local changes.");
    }
  };

  const onImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await importSyncDocument(file, resolveConflictsAsync);
      setMsg(
        t("sync.mergeDone", { pulled: r.pulled, pushed: r.pushed, conflicts: r.conflicts }),
      );
    } catch (e) {
      setMsg(t("sync.mergeFail", { error: e instanceof Error ? e.message : String(e) }));
    } finally {
      setBusy(false);
    }
  };

  const onConnectGoogle = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await identity.connectGoogle();
      setMsg(t("sync.googleConnected"));
    } catch (e) {
      setMsg(t("sync.googleConnectFail", { error: e instanceof Error ? e.message : String(e) }));
    } finally {
      setBusy(false);
    }
  };

  const onDisconnectGoogle = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await identity.disconnect();
      setMsg(t("sync.googleDisconnected"));
    } catch (e) {
      setMsg(t("sync.disconnectFail", { error: e instanceof Error ? e.message : String(e) }));
    } finally {
      setBusy(false);
    }
  };

  const onDriveSync = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await syncWithGoogleDrive(resolveConflictsAsync);
      setMsg(
        t("sync.driveDone", { pulled: r.pulled, pushed: r.pushed, conflicts: r.conflicts }),
      );
      // 마지막 동기화 시각 표시를 갱신한다(syncWithGoogleDrive가 settings에 기록함).
      setSettings(await storage.getSettings());
    } catch (e) {
      setMsg(t("sync.driveFail", { error: e instanceof Error ? e.message : String(e) }));
    } finally {
      setBusy(false);
    }
  };

  const onExternalSync = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const store = createExternalObjectStore(externalProfile);
      const r = await syncWithRemoteObjectStore(store, resolveConflictsAsync);
      setMsg(
        `Synced ${externalProfile.provider}: pulled ${r.pulled}, pushed ${r.pushed}, conflicts ${r.conflicts}.`,
      );
      setSettings(await storage.getSettings());
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onParseRclone = () => {
    const remotes = parseRcloneConfig(rcloneText);
    setRcloneRemotes(remotes);
    const firstSupported = remotes.find((remote) =>
      remote.providerId && DIRECT_PROVIDER_IDS.includes(remote.providerId as DirectProviderId),
    );
    if (firstSupported) {
      setExternalProfile((current) => profileFromRcloneRemote(current, firstSupported));
      setMsg(`Imported rclone remote "${firstSupported.name}" as ${firstSupported.providerId}.`);
    } else {
      setMsg("No browser-direct compatible rclone remote found.");
    }
  };

  const providerDescriptor = STORAGE_PROVIDER_CATALOG.find((provider) => provider.id === externalProfile.provider);
  const providerReady = providerDescriptor?.status === "ready";

  return (
    <section>
      {pendingConflicts && (
        <SyncConflictModal
          conflicts={pendingConflicts.conflicts}
          onResolve={handleResolveConflicts}
          onCancel={handleCancelConflicts}
        />
      )}
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-faint">
        {t("sync.title")}
      </h2>
      <div className="space-y-3 rounded-xl border border-line bg-bg-soft p-3">
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          className="hidden"
          onChange={onImport}
        />
        <p className="text-xs text-ink-faint">
          {t("sync.mergeDesc")}
        </p>

        {/* Google Drive 동기화(결정 #8) */}
        <div className="space-y-2 rounded-lg border border-line bg-bg-raised/40 p-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-ink-soft">Google Drive</span>
            {connected ? (
              <span className="truncate text-xs text-emerald-300">
                {t("sync.connected")}{session.email ? ` · ${session.email}` : ""}
              </span>
            ) : (
              <span className="text-xs text-ink-faint">{t("sync.notConnected")}</span>
            )}
          </div>

          {!googleConfigured ? (
            <p className="text-xs text-ink-faint">
              {t("sync.googleNotConfigured")}
            </p>
          ) : connected ? (
            <div className="space-y-2">
              <div className="flex gap-2">
                <button
                  className="flex-1 rounded-lg bg-accent px-3 py-2 text-sm font-medium text-white hover:bg-accent-soft disabled:opacity-50"
                  onClick={onDriveSync}
                  disabled={busy}
                >
                  {t("sync.syncWithDrive")}
                </button>
                <button
                  className="rounded-lg border border-line px-3 py-2 text-sm text-ink-soft hover:bg-bg-raised disabled:opacity-50"
                  onClick={onDisconnectGoogle}
                  disabled={busy}
                >
                  {t("sync.disconnect")}
                </button>
              </div>

              {/* 자동 동기화 토글(결정 #20: 디바운스 자동 동기화). 변경이 멈추면
                  잠시 뒤 자동으로 Drive와 병합한다. 충돌은 "내 변경 우선"으로 조용히
                  해소한다(수동 버튼은 위 우선순위 선택을 따른다). */}
              <label className="flex items-start justify-between gap-3 rounded-lg bg-bg-raised/40 px-2 py-2">
                <span className="min-w-0">
                  <span className="block text-sm text-ink-soft">{t("sync.autoSync")}</span>
                  <span className="mt-0.5 block text-xs text-ink-faint">
                    {t("sync.autoSyncDesc")}
                  </span>
                </span>
                <button
                  role="switch"
                  aria-checked={autoOn}
                  aria-label={t("sync.autoSync")}
                  onClick={() => void onToggleAuto(!autoOn)}
                  disabled={busy || !settings}
                  className={[
                    "relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-50",
                    autoOn ? "bg-accent" : "bg-bg-raised",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform",
                      autoOn ? "translate-x-5" : "translate-x-0",
                    ].join(" ")}
                  />
                </button>
              </label>

              {autoOn && (
                <div className="flex items-center justify-end px-2">
                  <select
                    className="rounded-lg border border-line bg-bg-soft px-2 py-1 text-xs text-ink-soft outline-none focus:border-accent"
                    value={settings?.sync.debounceMs ?? 10000}
                    onChange={(e) => void onChangeInterval(Number(e.target.value))}
                    disabled={busy || !settings}
                  >
                    <option value={10000}>{t("sync.interval.10s")}</option>
                    <option value={60000}>{t("sync.interval.1m")}</option>
                    <option value={300000}>{t("sync.interval.5m")}</option>
                    <option value={600000}>{t("sync.interval.10m")}</option>
                    <option value={1800000}>{t("sync.interval.30m")}</option>
                    <option value={3600000}>{t("sync.interval.1h")}</option>
                  </select>
                </div>
              )}

              {lastSyncedAt && (
                <p className="text-xs text-ink-faint">
                  {t("sync.lastSynced", { time: new Date(lastSyncedAt).toLocaleString(locale) })}
                </p>
              )}
            </div>
          ) : (
            <button
              className="w-full rounded-lg border border-line px-3 py-2 text-sm text-ink-soft hover:bg-bg-raised disabled:opacity-50"
              onClick={onConnectGoogle}
              disabled={busy}
            >
              {t("sync.connectGoogle")}
            </button>
          )}
        </div>

        {/* Direct provider sync: browser/app talks to the provider without a RoQeet file proxy. */}
        <div className="space-y-3 rounded-lg border border-line bg-bg-raised/40 p-2">
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-ink-soft">Direct storage</span>
              <span className="text-xs text-ink-faint">browser/app &lt;-&gt; provider</span>
            </div>
            <p className="text-xs text-ink-faint">
              Credentials stay in this browser. RoQeet servers are not used as a file proxy.
            </p>
          </div>

          <label className={syncLabelCls}>
            Provider
            <select
              className={syncInputCls}
              value={externalProfile.provider}
              onChange={(event) =>
                setExternalProfile((current) => ({
                  ...current,
                  provider: event.target.value as DirectProviderId,
                }))
              }
            >
              {DIRECT_PROVIDER_IDS.map((id) => {
                const descriptor = STORAGE_PROVIDER_CATALOG.find((provider) => provider.id === id);
                return (
                  <option key={id} value={id}>
                    {descriptor?.label ?? id}
                    {descriptor?.status === "blocked" ? " (SDK needed)" : ""}
                  </option>
                );
              })}
            </select>
          </label>

          {providerDescriptor && (
            <p className="rounded-lg bg-bg-soft px-2 py-2 text-xs text-ink-faint">
              <span className="font-medium text-ink-soft">{providerDescriptor.status}</span>
              {" - "}
              {providerDescriptor.notes}
            </p>
          )}

          <label className={syncLabelCls}>
            Root path
            <input
              className={syncInputCls}
              value={externalProfile.rootPath}
              onChange={(event) =>
                setExternalProfile((current) => ({ ...current, rootPath: event.target.value }))
              }
              placeholder="RoQeet"
            />
          </label>

          {externalProfile.provider === "github" && (
            <div className="grid gap-2 sm:grid-cols-2">
              <label className={syncLabelCls}>
                Owner
                <input
                  className={syncInputCls}
                  value={externalProfile.owner}
                  onChange={(event) => setExternalProfile((current) => ({ ...current, owner: event.target.value }))}
                  placeholder="developer-layer"
                />
              </label>
              <label className={syncLabelCls}>
                Repository
                <input
                  className={syncInputCls}
                  value={externalProfile.repo}
                  onChange={(event) => setExternalProfile((current) => ({ ...current, repo: event.target.value }))}
                  placeholder="roqeet"
                />
              </label>
              <label className={syncLabelCls}>
                Branch
                <input
                  className={syncInputCls}
                  value={externalProfile.branch}
                  onChange={(event) => setExternalProfile((current) => ({ ...current, branch: event.target.value }))}
                  placeholder="main"
                />
              </label>
              <label className={syncLabelCls}>
                Token
                <input
                  className={syncInputCls}
                  type="password"
                  value={externalProfile.accessToken}
                  onChange={(event) =>
                    setExternalProfile((current) => ({ ...current, accessToken: event.target.value }))
                  }
                  placeholder="github_pat_..."
                />
              </label>
            </div>
          )}

          {externalProfile.provider === "webdav" && (
            <div className="grid gap-2 sm:grid-cols-2">
              <label className={`${syncLabelCls} sm:col-span-2`}>
                WebDAV URL
                <input
                  className={syncInputCls}
                  value={externalProfile.baseUrl}
                  onChange={(event) => setExternalProfile((current) => ({ ...current, baseUrl: event.target.value }))}
                  placeholder="https://cloud.example.com/remote.php/dav/files/user"
                />
              </label>
              <label className={syncLabelCls}>
                Username
                <input
                  className={syncInputCls}
                  value={externalProfile.username}
                  onChange={(event) => setExternalProfile((current) => ({ ...current, username: event.target.value }))}
                />
              </label>
              <label className={syncLabelCls}>
                Password
                <input
                  className={syncInputCls}
                  type="password"
                  value={externalProfile.password}
                  onChange={(event) => setExternalProfile((current) => ({ ...current, password: event.target.value }))}
                />
              </label>
              <label className={`${syncLabelCls} sm:col-span-2`}>
                Bearer token
                <input
                  className={syncInputCls}
                  type="password"
                  value={externalProfile.bearerToken}
                  onChange={(event) =>
                    setExternalProfile((current) => ({ ...current, bearerToken: event.target.value }))
                  }
                  placeholder="optional"
                />
              </label>
            </div>
          )}

          {["dropbox", "onedrive", "pcloud"].includes(externalProfile.provider) && (
            <div className="grid gap-2 sm:grid-cols-2">
              <label className={syncLabelCls}>
                Access token
                <input
                  className={syncInputCls}
                  type="password"
                  value={externalProfile.accessToken}
                  onChange={(event) =>
                    setExternalProfile((current) => ({ ...current, accessToken: event.target.value }))
                  }
                />
              </label>
              {externalProfile.provider === "onedrive" && (
                <label className={syncLabelCls}>
                  Drive ID
                  <input
                    className={syncInputCls}
                    value={externalProfile.driveId}
                    onChange={(event) =>
                      setExternalProfile((current) => ({ ...current, driveId: event.target.value }))
                    }
                    placeholder="optional"
                  />
                </label>
              )}
              {externalProfile.provider === "pcloud" && (
                <label className={syncLabelCls}>
                  API base URL
                  <input
                    className={syncInputCls}
                    value={externalProfile.apiBaseUrl}
                    onChange={(event) =>
                      setExternalProfile((current) => ({ ...current, apiBaseUrl: event.target.value }))
                    }
                    placeholder="https://api.pcloud.com"
                  />
                </label>
              )}
            </div>
          )}

          {externalProfile.provider === "s3-compatible" && (
            <div className="grid gap-2 sm:grid-cols-2">
              <label className={`${syncLabelCls} sm:col-span-2`}>
                Endpoint
                <input
                  className={syncInputCls}
                  value={externalProfile.endpoint}
                  onChange={(event) => setExternalProfile((current) => ({ ...current, endpoint: event.target.value }))}
                  placeholder="https://s3.example.com"
                />
              </label>
              <label className={syncLabelCls}>
                Bucket
                <input
                  className={syncInputCls}
                  value={externalProfile.bucket}
                  onChange={(event) => setExternalProfile((current) => ({ ...current, bucket: event.target.value }))}
                />
              </label>
              <label className={syncLabelCls}>
                Region
                <input
                  className={syncInputCls}
                  value={externalProfile.region}
                  onChange={(event) => setExternalProfile((current) => ({ ...current, region: event.target.value }))}
                />
              </label>
              <label className={syncLabelCls}>
                Access key
                <input
                  className={syncInputCls}
                  value={externalProfile.accessKeyId}
                  onChange={(event) =>
                    setExternalProfile((current) => ({ ...current, accessKeyId: event.target.value }))
                  }
                />
              </label>
              <label className={syncLabelCls}>
                Secret key
                <input
                  className={syncInputCls}
                  type="password"
                  value={externalProfile.secretAccessKey}
                  onChange={(event) =>
                    setExternalProfile((current) => ({ ...current, secretAccessKey: event.target.value }))
                  }
                />
              </label>
              <label className={`${syncLabelCls} sm:col-span-2`}>
                Session token
                <input
                  className={syncInputCls}
                  type="password"
                  value={externalProfile.sessionToken}
                  onChange={(event) =>
                    setExternalProfile((current) => ({ ...current, sessionToken: event.target.value }))
                  }
                  placeholder="optional"
                />
              </label>
              <label className="flex items-center gap-2 text-xs text-ink-faint sm:col-span-2">
                <input
                  type="checkbox"
                  checked={externalProfile.forcePathStyle}
                  onChange={(event) =>
                    setExternalProfile((current) => ({ ...current, forcePathStyle: event.target.checked }))
                  }
                />
                Force path-style URLs
              </label>
            </div>
          )}

          <div className="space-y-2 rounded-lg bg-bg-soft p-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-ink-soft">rclone config import</span>
              <button
                className="rounded-lg border border-line px-2 py-1 text-xs text-ink-soft hover:bg-bg-raised disabled:opacity-50"
                onClick={onParseRclone}
                disabled={busy || !rcloneText.trim()}
              >
                Import
              </button>
            </div>
            <textarea
              className={`${syncInputCls} min-h-20 resize-y font-mono`}
              value={rcloneText}
              onChange={(event) => setRcloneText(event.target.value)}
              placeholder={"[myremote]\ntype = webdav\nurl = https://..."}
            />
            {rcloneRemotes.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {rcloneRemotes.map((remote) => (
                  <button
                    key={remote.name}
                    className="rounded-full border border-line px-2 py-1 text-xs text-ink-faint hover:bg-bg-raised"
                    onClick={() => setExternalProfile((current) => profileFromRcloneRemote(current, remote))}
                  >
                    {remote.name}: {remote.providerId ?? remote.type}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            className="w-full rounded-lg bg-accent px-3 py-2 text-sm font-medium text-white hover:bg-accent-soft disabled:opacity-50"
            onClick={onExternalSync}
            disabled={busy || !providerReady}
          >
            Sync with {providerDescriptor?.label ?? externalProfile.provider}
          </button>
        </div>

        {/* 파일 동기화(OAuth 불필요) */}
        <div className="space-y-2">
          <span className="block text-xs text-ink-soft">{t("sync.fileSyncLabel")}</span>
          <div className="flex gap-2">
            <button
              className="flex-1 rounded-lg border border-line px-3 py-2 text-sm text-ink-soft hover:bg-bg-raised disabled:opacity-50"
              onClick={onExport}
              disabled={busy}
            >
              {t("common.export")}
            </button>
            <button
              className="flex-1 rounded-lg border border-line px-3 py-2 text-sm text-ink-soft hover:bg-bg-raised disabled:opacity-50"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
            >
              {t("sync.importMerge")}
            </button>
          </div>
        </div>

        {msg && <p className="text-xs text-ink-soft">{msg}</p>}
      </div>
    </section>
  );
}
